import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Card, CardBody } from '@/components/ui/Card'
import { MoneyText } from '@/components/ui/MoneyText'
import { StatTile } from '@/components/ui/StatTile'
import { BarChart3, Camera, Clock, Gift, HandCoins, History, Landmark, ListChecks, PiggyBank, ReceiptText, Send, Users, Wallet } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { canWriteDepartment } from '@/auth/profile'
import { useProfile } from '@/auth/SessionProvider'
import { friendlyAuthError } from '@/data/auth'
import { listDepartments } from '@/data/departments'
import { listDepartmentMembers } from '@/data/members'
import { DeptInsights } from './DeptInsights'
import { greetingText } from './Greeting'
import { NavCardFace, navCardClass } from './NavCardFace'

function useDepartments() {
  return useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
}

function Loading() {
  return (
    <SkeletonRows rows={4} />
  )
}

function LoadError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <Card>
      <CardBody>
        <p role="alert" className="text-body text-bad">
          {friendlyAuthError(error)}
        </p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={retry}>
          Try again
        </Button>
      </CardBody>
    </Card>
  )
}

export { AdminHome } from './AdminHome'

/** Landing page for one department: a summary and a way into every screen. */
export function DeptHome({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const depts = useDepartments()
  const members = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })
  const dept = depts.data?.find((d) => d.id === deptId)
  const canWrite = canWriteDepartment(profile, deptId)

  if (depts.isPending) return <Loading />
  if (depts.isError) return <LoadError error={depts.error} retry={() => void depts.refetch()} />

  const greeting = greetingText(profile.fullName)
  const current = (members.data ?? []).filter((m) => m.leftOn === null)
  const behind = current.filter((m) => m.arrearsNgwee > 0)
  const owed = behind.reduce((sum, m) => sum + m.arrearsNgwee, 0)

  // The central treasury is not a dues-collecting department: it has no members to chase
  // and nobody to remit to, so those screens are left out of its page.
  const isCentral = dept?.is_central === true
  const tiles: { to: '/d/$deptId/members' | '/d/$deptId/pay' | '/d/$deptId/payments' | '/d/$deptId/dues' | '/d/$deptId/arrears' | '/d/$deptId/budgets' | '/d/$deptId/expenses' | '/d/$deptId/remittances' | '/d/$deptId/sheets' | '/d/$deptId/report' | '/d/$deptId/projects' | '/d/$deptId/history'; icon: LucideIcon; title: string; hint: string; show: boolean }[] = [
    { to: '/d/$deptId/pay', icon: HandCoins, title: 'Record a payment', hint: 'Dues collected from a member', show: canWrite && !isCentral },
    { to: '/d/$deptId/members', icon: Users, title: 'Members', hint: 'Add people and see who owes', show: !isCentral },
    { to: '/d/$deptId/payments', icon: ReceiptText, title: 'Payments', hint: 'What came in, month by month', show: !isCentral },
    { to: '/d/$deptId/arrears', icon: Clock, title: 'Who is behind', hint: 'Members with unpaid dues', show: !isCentral },
    { to: '/d/$deptId/dues', icon: ListChecks, title: 'Dues plans', hint: 'How much, how often, who pays', show: !isCentral },
    { to: '/d/$deptId/expenses', icon: Wallet, title: 'Expenses', hint: 'Record and track money spent', show: true },
    { to: '/d/$deptId/remittances', icon: Send, title: 'Money sent to central', hint: 'Cash and deposits you hand over', show: !isCentral },
    { to: '/d/$deptId/sheets', icon: Camera, title: 'Collection sheets', hint: 'Photo proof for the cash you collect', show: !isCentral },
    { to: '/d/$deptId/report', icon: BarChart3, title: 'Reports', hint: 'A quarterly report or a monthly statement: money in, spending, budget and cash', show: true },
    { to: '/d/$deptId/projects', icon: Gift, title: 'Projects', hint: 'Special giving for uniforms, trips and events', show: true },
    { to: '/d/$deptId/budgets', icon: PiggyBank, title: 'Budgets', hint: 'Plan what the department will spend each quarter', show: true },
    { to: '/d/$deptId/history', icon: History, title: 'History', hint: 'Who added, changed or deleted what, and when', show: true },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-neutral">
          <span className="font-medium">{greeting.hello}</span>
          <span className="text-body text-muted"> · {greeting.today}</span>
        </p>
        <h2 className="mt-1 text-title font-semibold text-ink">{dept?.name ?? 'Department'}</h2>
        <p className="mt-1 text-muted">
          {isCentral
            ? 'The church treasury: its own budget, spending and projects.'
            : canWrite ? 'You can record payments and manage members here.' : 'You can look at this department but not change it.'}
        </p>
      </div>

      {!isCentral && (
        <div className="grid grid-cols-2 gap-3" aria-label="This department at a glance">
          <Link to="/d/$deptId/members" params={{ deptId }} className="block rounded-card">
            <StatTile interactive label="Members" value={members.isPending ? undefined : String(current.length)} />
          </Link>
          <Link to="/d/$deptId/arrears" params={{ deptId }} className="block rounded-card">
            <StatTile
              interactive
              label="Owed in dues"
              value={members.isPending ? undefined : <MoneyText ngwee={owed} />}
              tone={owed > 0 ? 'warning' : 'default'}
              detail={!members.isPending && behind.length > 0 ? `${behind.length} ${behind.length === 1 ? 'person' : 'people'} behind` : undefined}
            />
          </Link>
        </div>
      )}
      {!isCentral && members.isError && <LoadError error={members.error} retry={() => void members.refetch()} />}

      <DeptInsights deptId={deptId} isCentral={isCentral} canWrite={canWrite} />

      <ul className="grid gap-3 sm:grid-cols-2">
        {isCentral && profile.isChurchReader && (
          <li>
            <Link to="/admin/income" className={navCardClass}>
              <NavCardFace icon={Landmark} title="Church income" description="Tithes and offerings counted each Sabbath, and what is passed on" />
            </Link>
          </li>
        )}
        {tiles.filter((x) => x.show).map((x) => (
          <li key={x.to}>
            <Link to={x.to} params={{ deptId }} className={navCardClass}>
              <NavCardFace icon={x.icon} title={x.title} description={x.hint} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export { AccountPage } from './AccountPage'
